import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ProgressBar from '../components/ProgressBar';
import QuestionCard from '../components/QuestionCard';
import { questions, VOICE_NOTES, GROUPS, resolveQuestion } from '../data/questions';

export default function Assessment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = user?.name ? user.name.split(' ')[0] : '';
  const [phase, setPhase] = useState('loading'); // loading | locked | welcome | question | voiceNote | submitting
  const [currentQ, setCurrentQ] = useState(1);
  const [answers, setAnswers] = useState({}); // { [questionNumber]: selectedOption }
  const [voiceNoteIdx, setVoiceNoteIdx] = useState(null);
  const [canRetakeAfter, setCanRetakeAfter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [versionConfig, setVersionConfig] = useState(null);

  const selectedOption = answers[currentQ] || null;

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      const { data } = await api.get('/assessment/status');
      if (data.status === 'not_started') {
        setPhase('welcome');
      } else if (data.status === 'in_progress') {
        setCurrentQ(data.currentQuestion);
        if (data.versionConfig) setVersionConfig(data.versionConfig);
        // Pre-load saved answers if returned by server
        if (data.answers) {
          const map = {};
          data.answers.forEach(a => { map[a.questionNumber] = a.selectedOption; });
          setAnswers(map);
        }
        setPhase('question');
      } else if (data.status === 'completed') {
        if (data.canRetake) {
          setPhase('welcome');
        } else {
          setCanRetakeAfter(data.canRetakeAfter);
          setPhase('locked');
        }
      }
    } catch {
      setPhase('welcome');
    }
  };

  const handleStart = async () => {
    try {
      const { data } = await api.post('/assessment/start');
      setCurrentQ(1);
      setAnswers({});
      if (data.versionConfig) setVersionConfig(data.versionConfig);
      setPhase('question');
    } catch (e) {
      setError('Could not start assessment. Please try again.');
    }
  };

  const handleSelect = (optionId) => {
    setAnswers(prev => ({ ...prev, [currentQ]: optionId }));
  };

  const handleBack = () => {
    if (currentQ <= 1) return;
    setError('');
    setCurrentQ(q => q - 1);
  };

  const handleNext = async () => {
    if (!selectedOption || saving) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/assessment/answer', {
        questionNumber: currentQ,
        selectedOption,
      });

      // Check if we hit a voice note boundary
      if ([5, 10, 15, 20].includes(currentQ)) {
        setVoiceNoteIdx(currentQ / 5);
        setPhase('voiceNote');
      } else if (data.completed) {
        navigate('/assessment/result');
      } else {
        setCurrentQ(data.nextQuestion);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save answer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleVoiceNoteContinue = async () => {
    if (voiceNoteIdx === 4) {
      navigate('/assessment/result');
    } else {
      const next = voiceNoteIdx * 5 + 1;
      setCurrentQ(next);
      setPhase('question');
      setVoiceNoteIdx(null);
    }
  };

  // Resolve the current question using versionConfig (versions + shuffle)
  const currentQuestion = resolveQuestion(currentQ, versionConfig);
  const rawQuestion = questions.find(q => q.id === currentQ);
  const group = rawQuestion ? GROUPS.find(g => g.id === rawQuestion.group) : null;

  if (phase === 'loading') {
    return (
      <div style={s.page}>
        <div style={s.center}><div style={s.spinner} /></div>
      </div>
    );
  }

  if (phase === 'locked') {
    const date = canRetakeAfter ? new Date(canRetakeAfter).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.lockedIcon}>🔒</div>
          <h2 style={s.lockedTitle}>Assessment Locked</h2>
          <p style={s.lockedText}>You have completed this assessment. To maintain the integrity of your result, retakes are only available after a set period.</p>
          {date && <p style={s.lockedDate}>You can retake this assessment from <strong>{date}</strong>.</p>}
          <button style={s.btnPrimary} onClick={() => navigate('/assessment/result')}>View My Result</button>
        </div>
      </div>
    );
  }

  if (phase === 'welcome') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.welcomeIcon}>🧭</div>
          <h1 style={s.welcomeTitle}>{firstName ? `${firstName}, Find Your Practice Area` : 'Find Your Practice Area'}</h1>
          <p style={s.welcomeSubtitle} ><em>"Most law students pick their practice area by accident. You do not have to."</em></p>
          <div style={s.welcomeMsg}>
            <p style={s.welcomeP}>A 20-question self-assessment that goes deeper than grades and internships, into your temperament and your genuine readiness. Discover where you truly belong before the profession decides for you.</p>
            <p style={s.welcomeP}>There are no right or wrong answers. Answer honestly, not how you think you should answer, but how you actually are right now.</p>
            <p style={s.welcomeP}>The assessment takes about 10 to 15 minutes. You can exit and resume at any point. Your progress is saved automatically.</p>
            <p style={{ ...s.welcomeP, fontStyle: 'italic', color: 'var(--text-3)' }}>From Nitin: I have designed this assessment based on years of working with and mentoring law students. Use it as a mirror, not a verdict.</p>
          </div>
          {error && <p style={s.error}>{error}</p>}
          <button style={s.btnPrimary} onClick={handleStart}>Begin Assessment</button>
        </div>
      </div>
    );
  }

  if (phase === 'voiceNote' && voiceNoteIdx) {
    const note = VOICE_NOTES[voiceNoteIdx];
    return (
      <div style={s.page}>
        <div style={s.card}>
          <img src="/nitin-potdar-profile.png" alt="Nitin Potdar" style={s.vnPhoto} />
          <h2 style={s.vnTitle}>{note.title}</h2>
          <div style={s.vnBody}>
            {note.text.split('\n\n').map((para, i) => (
              <p key={i} style={s.vnPara}>{para}</p>
            ))}
          </div>
          <button style={s.btnPrimary} onClick={handleVoiceNoteContinue}>{note.button} &rarr;</button>
        </div>
      </div>
    );
  }

  if (phase === 'question' && currentQuestion) {
    return (
      <div style={s.page}>
        <div style={s.questionCard}>
          <ProgressBar currentQuestion={currentQ} />
          {group && (
            <div style={s.groupBadge}>
              <span style={s.groupTitle}>{group.title}</span>
              <span style={s.groupSub}>{group.subtitle}</span>
            </div>
          )}
          <QuestionCard
            key={currentQ}
            question={currentQuestion}
            selectedOption={selectedOption}
            onSelect={handleSelect}
          />
          {error && <p style={s.error}>{error}</p>}
          <div style={s.footer}>
            {currentQ > 1 && (
              <button style={s.btnBack} onClick={handleBack} disabled={saving}>
                ← Back
              </button>
            )}
            <button
              style={{ ...s.btnPrimary, opacity: selectedOption && !saving ? 1 : 0.45, cursor: selectedOption && !saving ? 'pointer' : 'not-allowed', flex: 1 }}
              disabled={!selectedOption || saving}
              onClick={handleNext}
            >
              {saving ? 'Saving...' : currentQ === 20 ? 'Submit' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

const s = {
  page: {
    minHeight: 'calc(100vh - 60px)', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    padding: '20px 16px', background: 'var(--bg)',
  },
  card: {
    background: '#fff', borderRadius: '20px', boxShadow: 'var(--shadow-lg)',
    padding: '36px 32px', maxWidth: '600px', width: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', textAlign: 'center',
  },
  questionCard: {
    background: '#fff', borderRadius: '20px', boxShadow: 'var(--shadow-lg)',
    maxWidth: '680px', width: '100%', display: 'flex', flexDirection: 'column',
    overflow: 'hidden', minHeight: '520px',
  },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  spinner: {
    width: '28px', height: '28px', borderRadius: '50%',
    border: '2.5px solid var(--border)', borderTop: '2.5px solid var(--navy)',
    animation: 'spin 0.8s linear infinite',
  },
  lockedIcon: { fontSize: '40px' },
  lockedTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  lockedText: { fontSize: '14px', color: 'var(--text-2)', lineHeight: '1.65', maxWidth: '420px' },
  lockedDate: { fontSize: '14px', color: 'var(--text)', background: 'var(--gold-light)', padding: '10px 18px', borderRadius: '10px', border: '1px solid var(--gold-mid)' },
  welcomeIcon: { fontSize: '44px' },
  welcomeTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '26px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  welcomeSubtitle: { fontSize: '14px', color: 'var(--gold)', marginTop: '-8px' },
  welcomeMsg: { textAlign: 'left', background: 'var(--bg)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border)', width: '100%', boxSizing: 'border-box' },
  welcomeP: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.7', margin: '0 0 12px 0' },
  vnIcon: { fontSize: '36px' },
  vnPhoto: { width: '104px', height: '104px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top center', border: '2.5px solid var(--gold-mid)', boxShadow: '0 2px 10px rgba(12,35,64,0.12)' },
  vnTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  vnBody: { textAlign: 'left', background: 'var(--bg)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border)', width: '100%', boxSizing: 'border-box' },
  vnPara: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.75', margin: '0 0 14px 0' },
  groupBadge: {
    display: 'flex', flexDirection: 'column', gap: '2px',
    padding: '12px 20px', borderBottom: '1px solid var(--border-2)', background: 'var(--gold-light)',
  },
  groupTitle: { fontSize: '13px', fontWeight: '700', color: 'var(--navy)' },
  groupSub: { fontSize: '11px', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  footer: { padding: '16px 20px', borderTop: '1px solid var(--border-2)', background: '#fff', display: 'flex', gap: '10px' },
  btnPrimary: {
    background: 'var(--navy)', color: '#fff', border: 'none',
    padding: '13px 28px', borderRadius: '10px', fontSize: '15px',
    fontWeight: '700', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", transition: 'filter 0.15s',
  },
  btnBack: {
    background: '#fff', color: 'var(--navy)', border: '1.5px solid var(--border)',
    padding: '13px 20px', borderRadius: '10px', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer', flexShrink: 0,
    fontFamily: "'DM Sans', sans-serif", transition: 'background 0.15s',
  },
  error: { fontSize: '13px', color: 'var(--danger)', margin: '0 20px', background: 'var(--danger-bg)', padding: '10px 14px', borderRadius: '8px', textAlign: 'left' },
};
